import { FieldValues, FormState, UseFormReset } from 'react-hook-form';
import { Price, updateProductsPricing } from 'src/api/updateProductsPricing/updateProductsPricing';
import { DELIMITER } from './constants';
import { IItemIdWithPrices } from './types';
import { SnackbarStatus } from '@weareopenr/component-library';
import { SnackbarContextValue } from '@weareopenr/component-library/build/components/Snackbar/types';

export const submitPrices =
  <TFieldValues extends FieldValues>(
    dirtyFields: FormState<TFieldValues>['dirtyFields'],
    products: IItemIdWithPrices[],
    resetForm: UseFormReset<TFieldValues>,
    showSnackbar: SnackbarContextValue['showSnackbar'],
    invalidateQueries: () => Promise<void>,
    priceSchemeId: string,
  ) =>
  async (data: TFieldValues) => {
    const requestBody: Price[] = [];
    const updatedKeys = Object.keys(dirtyFields);

    updatedKeys.forEach((key) => {
      const [itemId, extensionInstanceId, priceBandId] = key.split(DELIMITER);
      const newPrice = data[key];
      const product = products.find((product) => product.item_id === itemId);
      const existingPrice = product?.prices.find(
        (price) =>
          String(price.price_band_id) === priceBandId && String(price.extension_instance_id) === extensionInstanceId,
      );

      // The form key carries the string 'null' for the item/band default, not a real null.
      const extensionInstanceIdValue = extensionInstanceId === 'null' ? null : extensionInstanceId;
      const priceBandIdValue = priceBandId === 'null' ? null : priceBandId;

      if (existingPrice) {
        requestBody.push({
          price_id: existingPrice.price_id,
          item_id: itemId,
          extension_instance_id: extensionInstanceIdValue,
          price_band_id: priceBandIdValue,
          value_with_tax: newPrice === '' ? null : parseFloat(newPrice),
        });
      } else {
        requestBody.push({
          price_id: null,
          item_id: itemId,
          extension_instance_id: extensionInstanceIdValue,
          price_band_id: priceBandIdValue,
          value_with_tax: parseFloat(newPrice),
        });
      }
    });

    try {
      await updateProductsPricing(priceSchemeId, requestBody);

      resetForm(data, { keepValues: true });
      showSnackbar({ status: SnackbarStatus.SUCCESS, message: 'Changes have been successfully saved' });
      await invalidateQueries();
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : 'Something went wrong updating pricing',
      });
    }
  };
